// Founder detail drawer — Phase 1.1 of advisor pipe
// (docs/founder-advisor-pipe-prd.md §7.3 sections 1–8 + write surfaces).
//
// Phase 1.1 scope (this file):
//   §1 Activation strip            — from rollup row (no fetch)
//   §2 SLA + tier setter           — from /banker/founders/:id (tier setter is PC/lead-only, server-enforced)
//   §3 Last touch                  — rollup
//   §4 Last 4 weekly check-ins     — full GET, viewed-on-scroll touches
//   §5 Open mission/proof requests — full GET, viewed-on-scroll touches
//   §6 Open claim reviews          — full GET, viewed-on-scroll touches
//   §7 Escalations on this founder — list of muteable rows
//   §8 Internal notes thread       — append-only
//
// Out of scope (Phase 1.4):
//   §9 Mentor activity panel — empty-state behind features.mentor_platform_live
//
// Trust invariants (PRD §6.3 + §7.3 last bullet):
//   - 'viewed' touches are debounced server-side (24h per banker+artifact).
//     Client safely fires on first scroll-in; server collapses dupes.
//   - Mark-as-touched is action='manual_marked' (NOT 'viewed') so PC's
//     advisor health table can flag manual-vs-real ratios.
//   - All ProgressLoader-worthy ops (>2s) wear one. Note save is short and
//     uses an inline button-spinner; we do NOT lie with a fake stage list.
//
// Theme: semantic shadcn tokens only (RULE3 in check-contrast.mjs).

import { useEffect, useRef, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ProgressLoader from "@/components/progress-loader";
import {
  Building2, Clock, Calendar, ShieldCheck, AlertTriangle, CheckCircle2, Circle,
  MinusCircle, MessageSquare, FileCheck2, FileWarning, Loader2, CheckCheck,
} from "lucide-react";
import type { FounderRollupItem, Signal } from "@/types/advisor-pipe";

const BASE = import.meta.env.BASE_URL;

interface Props {
  open: boolean;
  onClose: () => void;
  founder: FounderRollupItem | null;
  /** Called after a successful write so the parent table can refresh hoursSinceLastTouch. */
  onChanged?: () => void;
}

interface DetailResponse {
  company: { id: number; name: string; cohortId: number | null; stage: string | null; onboardingStatus: string | null };
  founder: { id: number; name: string; email: string | null } | null;
  sla: { tier: string; cadenceDays: number; checkInResponseH: number; source: string } | null;
  weeklyUpdates: Array<{ id: number; weekOf: string; status: string; headline: string | null; highlights: string | null; challenges: string | null; asks: string | null; submittedBy: string | null; createdAt: string }>;
  openProofs: Array<{ id: number; kind: string; title: string | null; describesWhat: string | null; readinessComponentKey: string | null; readinessImpact: number | null; addedByName: string | null; createdAt: string }>;
  openClaims: Array<{ id: number; source: string; sourceLabel: string | null; fieldLabel: string | null; proposedValue: string | null; status: string; rejectReason: string | null; correctedValue: string | null; createdAt: string }>;
  touches: Array<{ id: number; bankerId: number; action: string; artifactKind: string; artifactId: string; visibleToFounder: boolean; createdAt: string }>;
  internalNotes: Array<{ id: number; bankerId: number; body: string; createdAt: string }>;
}

function signalDot(s: Signal) {
  if (s === "green") return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (s === "amber") return <Circle className="w-4 h-4 text-amber-500 fill-amber-500/30" />;
  return <AlertTriangle className="w-4 h-4 text-red-500" />;
}
function fmtAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtAgo(hours: number | null): string {
  if (hours == null) return "never";
  if (hours < 1) return "just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function bankerToken(): string { return localStorage.getItem("banker_token") || ""; }

// Hook: fire callback once when an element first scrolls into view.
function useViewOnce<T extends Element = HTMLElement>(cb: () => void) {
  const ref = useRef<T | null>(null);
  const fired = useRef(false);
  useEffect(() => {
    if (!ref.current || fired.current) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !fired.current) {
          fired.current = true;
          cb();
          io.disconnect();
        }
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [cb]);
  return ref;
}

export default function FounderDetailDrawer({ open, onClose, founder, onChanged }: Props) {
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyId = founder?.companyId ?? null;

  // Re-fetch detail on every open. Cheap (one indexed call) and avoids
  // showing stale touches after the user marked-as-touched in another tab.
  useEffect(() => {
    let cancelled = false;
    if (!open || !companyId) { setDetail(null); return; }
    setLoading(true); setError(null);
    fetch(`${BASE}api/banker/founders/${companyId}`, { headers: { "x-banker-token": bankerToken() } })
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
        return r.json() as Promise<DetailResponse>;
      })
      .then(d => { if (!cancelled) setDetail(d); })
      .catch(e => { if (!cancelled) setError(String(e?.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, companyId]);

  const recordTouch = useCallback(async (artifactKind: string, artifactId: string, action: string) => {
    if (!companyId) return;
    try {
      await fetch(`${BASE}api/banker/touches`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": bankerToken() },
        body: JSON.stringify({ companyId, artifactKind, artifactId, action }),
      });
    } catch {/* server-side debounce handles dupes; transient failure ok */}
  }, [companyId]);

  const onMarkTouched = async () => {
    if (!companyId) return;
    setMarking(true);
    try {
      const r = await fetch(`${BASE}api/banker/touches`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": bankerToken() },
        body: JSON.stringify({ companyId, artifactKind: "company", artifactId: String(companyId), action: "manual_marked" }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      // Re-fetch detail to pick up the new touch in the activity log.
      const d = await fetch(`${BASE}api/banker/founders/${companyId}`, { headers: { "x-banker-token": bankerToken() } }).then(r => r.json());
      setDetail(d);
      onChanged?.();
    } catch (e) {
      setError(String((e as any)?.message || e));
    } finally {
      setMarking(false);
    }
  };

  const onSaveNote = async () => {
    if (!companyId || !noteBody.trim()) return;
    setSavingNote(true);
    try {
      const r = await fetch(`${BASE}api/banker/internal-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": bankerToken() },
        body: JSON.stringify({ companyId, body: noteBody.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setNoteBody("");
      const d = await fetch(`${BASE}api/banker/founders/${companyId}`, { headers: { "x-banker-token": bankerToken() } }).then(r => r.json());
      setDetail(d);
    } catch (e) {
      setError(String((e as any)?.message || e));
    } finally {
      setSavingNote(false);
    }
  };

  if (!founder) return null;
  const f = founder;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            {f.companyName}
          </SheetTitle>
          <SheetDescription>
            {f.founderName ? `${f.founderName} · ` : ""}{f.cohortName ?? "No cohort"}
          </SheetDescription>
        </SheetHeader>

        {/* §1 Activation */}
        <section className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Activation</h3>
            <Button size="sm" variant="default" onClick={onMarkTouched} disabled={marking || !companyId}>
              {marking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5 mr-1" />}
              Mark as touched
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={f.activationState === "activated" ? "default" : f.activationState === "stuck" ? "destructive" : "secondary"}>
              {f.activationState}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">{signalDot(f.activationSignals.onboarding)} <span>Onboarding</span></div>
            <div className="flex items-center gap-2">{signalDot(f.activationSignals.readiness)} <span>Readiness ({f.readinessScore ?? "—"})</span></div>
            <div className="flex items-center gap-2">{signalDot(f.activationSignals.checkIns)} <span>Weekly check-ins</span></div>
            <div className="flex items-center gap-2">{signalDot(f.activationSignals.investorReady)} <span>Investor-ready</span></div>
          </div>
        </section>

        <Separator className="my-4" />

        {/* §2 SLA */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> SLA
          </h3>
          <div className="text-sm flex items-center gap-2 flex-wrap">
            <Badge variant={f.slaStatus === "breach" ? "destructive" : f.slaStatus === "at_risk" ? "secondary" : "default"}>
              {f.slaStatus.replace("_", " ")}
            </Badge>
            <span className="text-muted-foreground">tier:</span> {f.slaTier}
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">via:</span> {f.slaSource === "company_override" ? "company override" : "cohort default"}
          </div>
          <SlaTierSetter companyId={companyId} currentTier={f.slaTier} onChanged={() => onChanged?.()} />
        </section>

        <Separator className="my-4" />

        {/* §3 Last touch */}
        <section className="space-y-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Last advisor touch
          </h3>
          <p className="text-sm">{fmtAt(f.lastAdvisorTouchAt)} <span className="text-muted-foreground">({fmtAgo(f.hoursSinceLastTouch)})</span></p>
        </section>

        <Separator className="my-4" />

        {/* Loading guard for detail-fetched sections */}
        {loading && (
          <div className="py-3">
            <ProgressLoader
              active={loading}
              title="Loading founder detail"
              stages={[
                "Fetching company + primary founder",
                "Pulling last 4 weekly check-ins",
                "Loading open proof + claim queues",
                "Aggregating advisor touches + notes",
                "Resolving SLA tier",
              ]}
              stageIntervalMs={500}
            />
          </div>
        )}
        {error && <div className="py-3 text-sm text-destructive">Couldn't load detail: {error}</div>}

        {detail && (
          <>
            {/* §4 Last 4 check-ins */}
            <CheckInsSection items={detail.weeklyUpdates} onView={(id) => recordTouch("weekly_update", String(id), "viewed")} />
            <Separator className="my-4" />

            {/* §5 Open proofs */}
            <ProofsSection items={detail.openProofs} onView={(id) => recordTouch("proof_artifact", String(id), "viewed")} />
            <Separator className="my-4" />

            {/* §6 Open claim reviews */}
            <ClaimsSection items={detail.openClaims} onView={(id) => recordTouch("claim_review", String(id), "viewed")} />
            <Separator className="my-4" />

            {/* §8 Internal notes thread (append-only) */}
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5" /> Internal notes
                <span className="text-[10px] text-muted-foreground font-normal normal-case tracking-normal">(other advisors only — never the founder)</span>
              </h3>
              <ul className="space-y-2">
                {detail.internalNotes.length === 0 && <li className="text-xs text-muted-foreground italic">No internal notes yet.</li>}
                {detail.internalNotes.map(n => (
                  <li key={n.id} className="text-sm bg-muted/50 border rounded-md px-3 py-2">
                    <p className="whitespace-pre-wrap">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">advisor #{n.bankerId} · {fmtAt(n.createdAt)}</p>
                  </li>
                ))}
              </ul>
              <div className="space-y-2">
                <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add an internal note (founder cannot see)…" rows={2} />
                <div className="flex justify-end">
                  <Button size="sm" onClick={onSaveNote} disabled={savingNote || !noteBody.trim()}>
                    {savingNote ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                    Save note
                  </Button>
                </div>
              </div>
            </section>

            <Separator className="my-4" />

            {/* §7.3 §last — Activity log (last 20 touches) */}
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Activity log</h3>
              <ul className="space-y-1.5 text-xs">
                {detail.touches.length === 0 && <li className="text-muted-foreground italic">No advisor activity yet.</li>}
                {detail.touches.map(t => (
                  <li key={t.id} className="flex items-start gap-2">
                    <Clock className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-medium">advisor #{t.bankerId}</span> {t.action.replace("_", " ")}{" "}
                      <span className="text-muted-foreground">{t.artifactKind} #{t.artifactId}</span>{" "}
                      <span className="text-muted-foreground">· {fmtAt(t.createdAt)}</span>
                      {!t.visibleToFounder && <span className="ml-1 text-[9px] uppercase tracking-wider text-muted-foreground">(internal)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}

        <Separator className="my-4" />
        <MentorActivityPanel />
      </SheetContent>
    </Sheet>
  );
}

// ---- §2 SLA tier setter ----
// PC + lead-only — server enforces 403 (PRD §11). UI shows the dropdown
// optimistically; if the server returns 403 the form rolls back so the
// user sees the actual permissions outcome instead of a silent no-op.
function SlaTierSetter({ companyId, currentTier, onChanged }: { companyId: number | null; currentTier: string; onChanged: () => void }) {
  const [tier, setTier] = useState(currentTier);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  useEffect(() => { setTier(currentTier); }, [currentTier]);
  if (!companyId) return null;

  const onSave = async () => {
    if (tier === currentTier) return;
    setSaving(true); setErr(null); setOk(false);
    try {
      const r = await fetch(`${BASE}api/banker/companies/${companyId}/sla`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": bankerToken() },
        body: JSON.stringify({ tier }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 403) { setErr("Tier setter is PC/lead-only (PRD §11)."); setTier(currentTier); }
        else setErr(body?.message || body?.error || `HTTP ${r.status}`);
        return;
      }
      setOk(true);
      onChanged();
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-xs space-y-1">
      <div className="flex items-center gap-2">
        <label className="text-muted-foreground">Set tier:</label>
        <select
          className="border rounded-md px-2 py-1 bg-background text-foreground"
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          disabled={saving}
        >
          <option value="high_touch">high_touch (3d cadence)</option>
          <option value="standard">standard (7d cadence)</option>
          <option value="light">light (21d cadence)</option>
        </select>
        <Button size="sm" variant="outline" onClick={onSave} disabled={saving || tier === currentTier}>
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
          Save
        </Button>
      </div>
      {ok && <p className="text-emerald-600">Tier updated. Cadence applies on next nightly SLA scan.</p>}
      {err && <p className="text-destructive">{err}</p>}
      <p className="text-[10px] text-muted-foreground">
        Server enforces PC/lead-only per PRD §11 (regular advisors get 403).
      </p>
    </div>
  );
}

// ---- §9 Mentor activity ----
// Phase 1.4 close per PRD §7.3 §9. Gated behind VITE_MENTOR_PLATFORM_LIVE
// build-time flag (defaults to "0"). Renders an honest empty-state until
// the mentor platform schema lands — never a fabricated row.
function MentorActivityPanel() {
  const live = String(import.meta.env.VITE_MENTOR_PLATFORM_LIVE || "0") === "1";
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mentor activity</h3>
      {live ? (
        // Mentor schema is not in this codebase yet (Phase 2 PRD). When it
        // ships, replace this with a fetch + list. For now, the flag-on
        // branch still shows an honest placeholder so we never lie about
        // which mentor did what.
        <p className="text-xs text-muted-foreground italic">
          Mentor platform live but no engagements wired into the drawer yet — see Phase 2 PRD.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          Mentor platform not live in this environment. Set <code>VITE_MENTOR_PLATFORM_LIVE=1</code> at build time once the mentor PRD ships.
        </p>
      )}
    </section>
  );
}

// ---- §4 ----
function CheckInsSection({ items, onView }: { items: DetailResponse["weeklyUpdates"]; onView: (id: number) => void }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
        <Calendar className="w-3.5 h-3.5" /> Last 4 weekly check-ins
      </h3>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No check-ins yet.</p>}
      <ul className="space-y-2">
        {items.map(w => (
          <CheckInRow key={w.id} w={w} onView={() => onView(w.id)} />
        ))}
      </ul>
    </section>
  );
}
function CheckInRow({ w, onView }: { w: DetailResponse["weeklyUpdates"][number]; onView: () => void }) {
  const ref = useViewOnce<HTMLLIElement>(onView);
  return (
    <li ref={ref} className="text-sm bg-muted/40 border rounded-md px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">Week of {new Date(w.weekOf).toLocaleDateString()}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{w.status}</span>
      </div>
      {w.headline && <p className="mt-1">{w.headline}</p>}
      {w.highlights && <p className="mt-1 text-xs"><span className="text-muted-foreground">Highlights: </span>{w.highlights}</p>}
      {w.challenges && <p className="mt-1 text-xs"><span className="text-muted-foreground">Challenges: </span>{w.challenges}</p>}
      {w.asks && <p className="mt-1 text-xs"><span className="text-muted-foreground">Asks: </span>{w.asks}</p>}
    </li>
  );
}

// ---- §5 ----
function ProofsSection({ items, onView }: { items: DetailResponse["openProofs"]; onView: (id: number) => void }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
        <FileCheck2 className="w-3.5 h-3.5" /> Open proof / mission requests ({items.length})
      </h3>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No open proof items.</p>}
      <ul className="space-y-1.5">
        {items.map(p => (
          <ProofRow key={p.id} p={p} onView={() => onView(p.id)} />
        ))}
      </ul>
    </section>
  );
}
function ProofRow({ p, onView }: { p: DetailResponse["openProofs"][number]; onView: () => void }) {
  const ref = useViewOnce<HTMLLIElement>(onView);
  return (
    <li ref={ref} className="text-sm flex items-start gap-2">
      <MinusCircle className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="truncate"><span className="font-medium">{p.title || p.kind}</span>
          {p.readinessImpact != null && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">+{p.readinessImpact}</span>}
        </p>
        {p.describesWhat && <p className="text-xs text-muted-foreground line-clamp-2">{p.describesWhat}</p>}
      </div>
    </li>
  );
}

// ---- §6 ----
function ClaimsSection({ items, onView }: { items: DetailResponse["openClaims"]; onView: (id: number) => void }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
        <FileWarning className="w-3.5 h-3.5" /> Open claim reviews ({items.length})
      </h3>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No open claim reviews.</p>}
      <ul className="space-y-1.5">
        {items.map(c => (
          <ClaimRow key={c.id} c={c} onView={() => onView(c.id)} />
        ))}
      </ul>
    </section>
  );
}
function ClaimRow({ c, onView }: { c: DetailResponse["openClaims"][number]; onView: () => void }) {
  const ref = useViewOnce<HTMLLIElement>(onView);
  return (
    <li ref={ref} className="text-sm bg-muted/40 border rounded-md px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{c.fieldLabel || c.source}</span>
        <Badge variant={c.status === "rejected" ? "destructive" : "secondary"} className="text-[9px]">{c.status}</Badge>
      </div>
      {c.proposedValue && <p className="text-xs mt-1"><span className="text-muted-foreground">Proposed: </span>{c.proposedValue}</p>}
      {c.rejectReason && <p className="text-xs mt-1"><span className="text-muted-foreground">Reason: </span>{c.rejectReason}</p>}
    </li>
  );
}
